import { useControllableState } from "../../hooks/file-upload/use-controllable-stare.hook";

import { useCallback } from "react";
import { type DropzoneProps, type FileRejection, useDropzone } from "react-dropzone";
import { useToast } from "../../hooks/use-toast";


interface FileUploaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Value of the uploader.
   * @type File[]
   * @default undefined
   * @example value={files}
   */
  value?: File[];

  /**
   * Function to be called when the value changes.
   * @type React.Dispatch<React.SetStateAction<File[]>>
   * @default undefined
   * @example onValueChange={(files) => setFiles(files)}
   */
  onValueChange: (files: File[]) => void;

  /**
   * Function to be called when files are uploaded.
   * @type (files: File[]) => Promise<void>
   * @default undefined
   * @example onUpload={(files) => uploadFiles(files)}
   */
  onUpload?: (files: File[]) => Promise<void>;

  /**
   * Progress of the uploaded files.
   * @type Record<string, number> | undefined
   * @default undefined
   * @example progresses={{ "file1.png": 50 }}
   */
  progresses?: Record<string, number>;

  /**
   * Accepted file types for the uploader.
   * @type { [key: string]: string[]}
   * @default
   * ```ts
   * { "image/*": [] }
   * ```
   * @example accept={["image/png", "image/jpeg"]}
   */
  accept?: DropzoneProps["accept"];

  /**
   * Maximum file size for the uploader.
   * @type number | undefined
   * @default 1024 * 1024 * 2 // 2MB
   * @example maxSize={1024 * 1024 * 2} // 2MB
   */
  maxSize?: DropzoneProps["maxSize"];

  /**
   * Maximum number of files for the uploader.
   * @type number | undefined
   * @default 1
   * @example maxFiles={5}
   */
  maxFiles?: DropzoneProps["maxFiles"];

  /**
   * Whether the uploader should accept multiple files.
   * @type boolean
   * @default false
   * @example multiple
   */
  multiple?: boolean;

  /**
   * Whether the uploader is disabled.
   * @type boolean
   * @default false
   * @example disabled
   */
  disabled?: boolean;
}

export function Uploader(props: FileUploaderProps) {
  const {
    value: valueProp,
    onValueChange,

    accept,
    maxSize = 1024 * 1024 * 2, // Default to 2MB if maxSize is undefined

    maxFiles,
    multiple,
    disabled = false,
    className,
    children,
  } = props;

  const {toast} = useToast();

  const [files, setFiles] = useControllableState({
    prop: valueProp,
    onChange: onValueChange,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );


    const updatedFiles = files ? [...files, ...newFiles] : newFiles;

    setFiles(updatedFiles);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    // biome-ignore lint/complexity/noForEach: <explanation>
    fileRejections.forEach(({ file }) => {
      if (file.size > maxSize) {
        toast({
          content: `Selected file exceeds the maximum size of ${maxSize / 1024 / 1024} MB.`,
          toastType: 'error',
        });
      }
    });
  }, [maxSize, toast]);

  const { getRootProps, getInputProps } = useDropzone({
    onDropRejected,
    onDrop,
    accept,
    maxFiles,
    disabled,
    multiple,
    maxSize,
  });

  return (
    <div {...getRootProps()} className={`${className} `}>
      <input {...getInputProps()} />
      {children}
    </div>
  );
}
